/** @sfdc-extension-file SNK */
import type { ShopperExperience } from '@/scapi';
import type { ComponentWithComponentData } from '@/lib/page-designer/component-loader.server';

/** JSON-serializable embedded component for fetcher resource routes. */
export type SerializableEmbeddedComponent = Omit<ComponentWithComponentData, 'componentData'> & {
    componentData?: Record<string, unknown>;
};

/**
 * Awaits nested component loader promises so an embedded host can be sent over a fetcher response.
 */
export async function resolveEmbeddedComponentData(
    component: ComponentWithComponentData
): Promise<SerializableEmbeddedComponent> {
    if (!component.componentData || Object.keys(component.componentData).length === 0) {
        return { ...component, componentData: {} };
    }

    const resolvedEntries = await Promise.all(
        Object.entries(component.componentData).map(async ([id, promise]) => [id, await promise] as const)
    );

    return {
        ...component,
        componentData: Object.fromEntries(resolvedEntries),
    };
}

/** Re-wraps resolved component data as promises for Region / EmbeddedComponentRegion on the client. */
export function hydrateEmbeddedComponentData(
    component: SerializableEmbeddedComponent
): ComponentWithComponentData {
    const componentData: Record<string, Promise<unknown>> = {};

    if (component.componentData) {
        for (const [id, value] of Object.entries(component.componentData)) {
            componentData[id] = Promise.resolve(value);
        }
    }

    return { ...component, componentData };
}

export function embeddedComponentRegionHasComponents(
    component: Pick<ShopperExperience.schemas['Component'], 'regions'> | null | undefined,
    regionId: string
): boolean {
    const region = component?.regions?.find((entry) => entry.id === regionId);
    return Boolean(region?.components?.length);
}
